/**
 * Generated by orval
 * Do not edit manually.
 * IP Fabric API
 * IP Fabric API documentation. For details see [IP Fabric API documentation](https://docs.ipfabric.io/main/IP_Fabric_API/)
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  GetDataPolicies200Item,
  GetDataPoliciesImpact200Item,
  GetDataPoliciesImpactParams,
  PostDataPolicies200,
  PostDataPoliciesBody,
  PostDataPoliciesImpactPreview200Item,
  PostDataPoliciesImpactPreviewBody,
  PutDataPoliciesId200,
  PutDataPoliciesIdBody,
} from '../../schemas';

import { apiClient } from '../../../apiClient';

/**
 * Lists label-based data policies with the roles they are assigned to.
 * @summary List data policies
 */
export const getDataPolicies = (signal?: AbortSignal) => {
  return apiClient<GetDataPolicies200Item[]>({
    url: `/data-policies`,
    method: 'GET',
    signal,
  });
};

export const getGetDataPoliciesQueryKey = () => {
  return [`/data-policies`] as const;
};

export const getGetDataPoliciesQueryOptions = <
  TData = Awaited<ReturnType<typeof getDataPolicies>>,
  TError = unknown,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getDataPolicies>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetDataPoliciesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getDataPolicies>>> = ({
    signal,
  }) => getDataPolicies(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getDataPolicies>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetDataPoliciesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getDataPolicies>>
>;
export type GetDataPoliciesQueryError = unknown;

export function useGetDataPolicies<
  TData = Awaited<ReturnType<typeof getDataPolicies>>,
  TError = unknown,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataPolicies>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataPolicies>>,
          TError,
          Awaited<ReturnType<typeof getDataPolicies>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDataPolicies<
  TData = Awaited<ReturnType<typeof getDataPolicies>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataPolicies>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataPolicies>>,
          TError,
          Awaited<ReturnType<typeof getDataPolicies>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDataPolicies<
  TData = Awaited<ReturnType<typeof getDataPolicies>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataPolicies>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List data policies
 */

export function useGetDataPolicies<
  TData = Awaited<ReturnType<typeof getDataPolicies>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataPolicies>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetDataPoliciesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Creates a label-based data policy. Condition labels must be enrolled for RBAC.
 * @summary Create data policy
 */
export const postDataPolicies = (
  postDataPoliciesBody: PostDataPoliciesBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostDataPolicies200>({
    url: `/data-policies`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postDataPoliciesBody,
    signal,
  });
};

export const getPostDataPoliciesMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDataPolicies>>,
    TError,
    { data: PostDataPoliciesBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postDataPolicies>>,
  TError,
  { data: PostDataPoliciesBody },
  TContext
> => {
  const mutationKey = ['postDataPolicies'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postDataPolicies>>,
    { data: PostDataPoliciesBody }
  > = (props) => {
    const { data } = props ?? {};

    return postDataPolicies(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostDataPoliciesMutationResult = NonNullable<
  Awaited<ReturnType<typeof postDataPolicies>>
>;
export type PostDataPoliciesMutationBody = PostDataPoliciesBody;
export type PostDataPoliciesMutationError = unknown;

/**
 * @summary Create data policy
 */
export const usePostDataPolicies = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postDataPolicies>>,
      TError,
      { data: PostDataPoliciesBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof postDataPolicies>>,
  TError,
  { data: PostDataPoliciesBody },
  TContext
> => {
  const mutationOptions = getPostDataPoliciesMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Updates a data policy and recomputes row access for all snapshots.
 * @summary Update data policy
 */
export const putDataPoliciesId = (
  id: string,
  putDataPoliciesIdBody: PutDataPoliciesIdBody,
) => {
  return apiClient<PutDataPoliciesId200>({
    url: `/data-policies/${id}`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: putDataPoliciesIdBody,
  });
};

export const getPutDataPoliciesIdMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putDataPoliciesId>>,
    TError,
    { id: string; data: PutDataPoliciesIdBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putDataPoliciesId>>,
  TError,
  { id: string; data: PutDataPoliciesIdBody },
  TContext
> => {
  const mutationKey = ['putDataPoliciesId'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putDataPoliciesId>>,
    { id: string; data: PutDataPoliciesIdBody }
  > = (props) => {
    const { id, data } = props ?? {};

    return putDataPoliciesId(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutDataPoliciesIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putDataPoliciesId>>
>;
export type PutDataPoliciesIdMutationBody = PutDataPoliciesIdBody;
export type PutDataPoliciesIdMutationError = unknown;

/**
 * @summary Update data policy
 */
export const usePutDataPoliciesId = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof putDataPoliciesId>>,
      TError,
      { id: string; data: PutDataPoliciesIdBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof putDataPoliciesId>>,
  TError,
  { id: string; data: PutDataPoliciesIdBody },
  TContext
> => {
  const mutationOptions = getPutDataPoliciesIdMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Deletes a data policy and removes it from every role it is assigned to.
 * @summary Delete data policy
 */
export const deleteDataPoliciesId = (id: string) => {
  return apiClient<unknown>({ url: `/data-policies/${id}`, method: 'DELETE' });
};

export const getDeleteDataPoliciesIdMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteDataPoliciesId>>,
    TError,
    { id: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteDataPoliciesId>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ['deleteDataPoliciesId'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteDataPoliciesId>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteDataPoliciesId(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteDataPoliciesIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteDataPoliciesId>>
>;

export type DeleteDataPoliciesIdMutationError = unknown;

/**
 * @summary Delete data policy
 */
export const useDeleteDataPoliciesId = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteDataPoliciesId>>,
      TError,
      { id: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteDataPoliciesId>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = getDeleteDataPoliciesIdMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Counts the guarded entities (devices, interfaces, ...) each saved data policy admits in a snapshot.
 * @summary Return data policies impact
 */
export const getDataPoliciesImpact = (
  params: GetDataPoliciesImpactParams,
  signal?: AbortSignal,
) => {
  return apiClient<GetDataPoliciesImpact200Item[]>({
    url: `/data-policies/impact`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetDataPoliciesImpactQueryKey = (
  params: GetDataPoliciesImpactParams,
) => {
  return [`/data-policies/impact`, ...(params ? [params] : [])] as const;
};

export const getGetDataPoliciesImpactQueryOptions = <
  TData = Awaited<ReturnType<typeof getDataPoliciesImpact>>,
  TError = unknown,
>(
  params: GetDataPoliciesImpactParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataPoliciesImpact>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetDataPoliciesImpactQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getDataPoliciesImpact>>
  > = ({ signal }) => getDataPoliciesImpact(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getDataPoliciesImpact>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetDataPoliciesImpactQueryResult = NonNullable<
  Awaited<ReturnType<typeof getDataPoliciesImpact>>
>;
export type GetDataPoliciesImpactQueryError = unknown;

export function useGetDataPoliciesImpact<
  TData = Awaited<ReturnType<typeof getDataPoliciesImpact>>,
  TError = unknown,
>(
  params: GetDataPoliciesImpactParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataPoliciesImpact>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataPoliciesImpact>>,
          TError,
          Awaited<ReturnType<typeof getDataPoliciesImpact>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDataPoliciesImpact<
  TData = Awaited<ReturnType<typeof getDataPoliciesImpact>>,
  TError = unknown,
>(
  params: GetDataPoliciesImpactParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataPoliciesImpact>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDataPoliciesImpact>>,
          TError,
          Awaited<ReturnType<typeof getDataPoliciesImpact>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetDataPoliciesImpact<
  TData = Awaited<ReturnType<typeof getDataPoliciesImpact>>,
  TError = unknown,
>(
  params: GetDataPoliciesImpactParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataPoliciesImpact>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Return data policies impact
 */

export function useGetDataPoliciesImpact<
  TData = Awaited<ReturnType<typeof getDataPoliciesImpact>>,
  TError = unknown,
>(
  params: GetDataPoliciesImpactParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDataPoliciesImpact>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetDataPoliciesImpactQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Counts the guarded entities an unsaved data policy condition would admit in a snapshot.
 * @summary Return data policy impact preview
 */
export const postDataPoliciesImpactPreview = (
  postDataPoliciesImpactPreviewBody: PostDataPoliciesImpactPreviewBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostDataPoliciesImpactPreview200Item[]>({
    url: `/data-policies/impact/preview`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postDataPoliciesImpactPreviewBody,
    signal,
  });
};

export const getPostDataPoliciesImpactPreviewMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postDataPoliciesImpactPreview>>,
    TError,
    { data: PostDataPoliciesImpactPreviewBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postDataPoliciesImpactPreview>>,
  TError,
  { data: PostDataPoliciesImpactPreviewBody },
  TContext
> => {
  const mutationKey = ['postDataPoliciesImpactPreview'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postDataPoliciesImpactPreview>>,
    { data: PostDataPoliciesImpactPreviewBody }
  > = (props) => {
    const { data } = props ?? {};

    return postDataPoliciesImpactPreview(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostDataPoliciesImpactPreviewMutationResult = NonNullable<
  Awaited<ReturnType<typeof postDataPoliciesImpactPreview>>
>;
export type PostDataPoliciesImpactPreviewMutationBody =
  PostDataPoliciesImpactPreviewBody;
export type PostDataPoliciesImpactPreviewMutationError = unknown;

/**
 * @summary Return data policy impact preview
 */
export const usePostDataPoliciesImpactPreview = <
  TError = unknown,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postDataPoliciesImpactPreview>>,
      TError,
      { data: PostDataPoliciesImpactPreviewBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof postDataPoliciesImpactPreview>>,
  TError,
  { data: PostDataPoliciesImpactPreviewBody },
  TContext
> => {
  const mutationOptions =
    getPostDataPoliciesImpactPreviewMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
